/**
 * The canonical slug for the default Copilot agent.
 * Used to determine if an agent is the built-in Copilot agent (whose display name
 * is feature-flagged as "Copilot cloud agent" or "Copilot coding agent").
 */
export const COPILOT_AGENT_SLUG = 'copilot-swe-agent'

/**
 * The slug for the Copilot Developer CLI agent.
 */
export const COPILOT_CLI_SLUG = 'copilot-developer-cli'

/**
 * Agent slugs that support subagent log grouping.
 */
const SUBAGENT_GROUPING_SLUGS: ReadonlySet<string> = new Set([COPILOT_AGENT_SLUG, COPILOT_CLI_SLUG])

/**
 * Checks whether an agent slug matches the built-in Copilot agent slug.
 * Note: this is tied to the canonical slug, not the display name, which is
 * feature-flagged separately.
 * @param agentSlug - The slug of the agent to check
 * @returns true if the agent matches the default Copilot agent slug
 */
export function isCopilotCodingAgent(agentSlug: string | undefined | null): boolean {
  return agentSlug === COPILOT_AGENT_SLUG
}

/**
 * Checks if an agent supports subagent log grouping (collapsible subagent sections).
 * @param agentSlug - The slug of the agent to check
 * @returns true if the agent supports subagent grouping
 */
export function agentSupportsSubagentGrouping(agentSlug: string | undefined | null): boolean {
  return !!agentSlug && SUBAGENT_GROUPING_SLUGS.has(agentSlug)
}

/**
 * Checks if the selected agent is Copilot or a custom agent (not a third-party agent).
 * Returns true for:
 * - Custom agents (no topLevelAgentId)
 * - Copilot cloud agent
 * - SubAgents
 * @param selectedAgent - The currently selected agent
 * @param agentSlug - The slug of the top-level agent (from agentInfo)
 * @returns true if the agent is Copilot or a custom agent
 */
export function isCopilotOrCustomAgent(
  selectedAgent: {topLevelAgentId?: string | null; subAgentSlug?: string | null},
  agentSlug: string | undefined,
): boolean {
  return !selectedAgent.topLevelAgentId || isCopilotCodingAgent(agentSlug) || !!selectedAgent.subAgentSlug
}

/**
 * Detects newly added agents when assignee selection changes.
 * Returns the first newly added agent assignee and the assignees that should be
 * saved immediately (without the modal).
 *
 * Note: The `nonAgentAssignees` return value includes:
 * - All non-agent assignees
 * - Any agent assignees that were already assigned before (i.e. not newly added)
 */
export function detectNewlyAddedAgent<TCurrent, TSelected = TCurrent>(
  currentAssignees: TCurrent[],
  selectedAssignees: TSelected[],
  isAgent: (assignee: TCurrent | TSelected) => boolean,
  getLogin: (assignee: TCurrent | TSelected) => string,
): {newlyAddedAgent: TSelected | undefined; nonAgentAssignees: TSelected[]} {
  const previousAgentLogins = new Set(currentAssignees.filter(isAgent).map(getLogin))
  const addedAgent = selectedAssignees.find(a => isAgent(a) && !previousAgentLogins.has(getLogin(a)))
  const assigneesToSave = selectedAssignees.filter(a => !isAgent(a) || previousAgentLogins.has(getLogin(a)))

  return {
    newlyAddedAgent: addedAgent,
    nonAgentAssignees: assigneesToSave,
  }
}
